import { context, trace, SpanStatusCode } from '@opentelemetry/api'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { Resource } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler
} from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import {
  SEMRESATTRS_DEPLOYMENT_ENVIRONMENT,
  SEMRESATTRS_SERVICE_NAME,
  SEMRESATTRS_SERVICE_NAMESPACE
} from '@opentelemetry/semantic-conventions'

const TRACER_NAME = 'birdmobil-website-customer'

/**
 * Turns the api base url into a matcher for propagateTraceHeaderCorsUrls.
 *
 * This matters more than it looks: the browser only attaches traceparent to
 * cross origin requests that match this list, and any url that matches will
 * trigger a CORS preflight the server has to answer with
 * `Access-Control-Allow-Headers: traceparent`. Keeping it scoped to our own api
 * means a misconfigured third party call can never be broken by tracing.
 */
const apiUrlMatcher = (baseUrl) => {
  if (!baseUrl) return []
  const escaped = baseUrl.replace(/\/+$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return [new RegExp(`^${escaped}`)]
}

/**
 * Builds the absolute /v1/traces endpoint from REACT_APP_SIGNOZ_URL.
 *
 * The backend services take a bare host (the scheme there is decided by an
 * insecure flag), so the same value gets pasted into the frontend env. The
 * browser exporter needs an absolute url and throws on anything else, so assume
 * https when no scheme is given — which matches how the backend treats it.
 */
const traceEndpoint = (rawUrl) => {
  const trimmed = rawUrl.replace(/\/+$/, '')
  const withScheme = /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`
  return `${withScheme}/v1/traces`
}

/**
 * Starts browser tracing and sends spans to signoz.
 *
 * Tracing stays off unless REACT_APP_SIGNOZ_URL is set, so shipping this build
 * changes nothing until the collector is reachable from the browser and the api
 * allows the traceparent header. Enabling it is a deploy time decision, not a
 * code change.
 */
const startTracing = () => {
  const collectorUrl = process.env.REACT_APP_SIGNOZ_URL
  if (!collectorUrl) {
    return
  }

  const propagateTraceHeaderCorsUrls = apiUrlMatcher(process.env.REACT_APP_API_BASE_URL)

  // The browser is normally the root of the trace, so this ratio decides for the
  // whole chain: the sampled flag travels in traceparent and every backend
  // service honours it through its own ParentBased sampler.
  const sampleRate = Number.parseFloat(process.env.REACT_APP_SIGNOZ_TRACE_SAMPLE_RATE)

  const provider = new WebTracerProvider({
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(Number.isFinite(sampleRate) ? sampleRate : 1)
    }),
    resource: new Resource({
      [SEMRESATTRS_SERVICE_NAME]: process.env.REACT_APP_SIGNOZ_SERVICE_NAME || TRACER_NAME,
      [SEMRESATTRS_SERVICE_NAMESPACE]:
        process.env.REACT_APP_SIGNOZ_SERVICE_NAMESPACE || 'birdmobil',
      [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: process.env.REACT_APP_ENV || 'development'
    })
  })

  provider.addSpanProcessor(
    new BatchSpanProcessor(new OTLPTraceExporter({ url: traceEndpoint(collectorUrl) }))
  )

  // ZoneContextManager keeps the active span across async boundaries, so a span
  // started in an event handler still parents the request it fires off.
  provider.register({ contextManager: new ZoneContextManager() })

  registerInstrumentations({
    instrumentations: [
      // axios uses XMLHttpRequest in the browser, so this is the one that
      // actually instruments our api calls; fetch covers everything else.
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls,
        clearTimingResources: true
      }),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls,
        clearTimingResources: true
      })
    ]
  })
}

/**
 * Public entry point — never lets telemetry take the page down.
 *
 * initTracing runs at module scope in index.js, so anything it throws (a
 * malformed collector url, a browser without the APIs the sdk expects) would
 * crash the whole app before the first render. Tracing is optional; the app is
 * not.
 */
export const initTracing = () => {
  try {
    startTracing()
  } catch (error) {
    console.error('[tracing] disabled, failed to initialise:', error?.message)
  }
}

/**
 * Records an error on the span that is currently active.
 *
 * Replaces Sentry.captureException: the failure lands on the same span as the
 * request that caused it, so signoz shows the error next to the call instead of
 * in a separate tool.
 */
export const recordError = (error, attributes = {}) => {
  const span = trace.getSpan(context.active())
  if (!span) return

  span.setAttributes(attributes)
  span.recordException(error)
  span.setStatus({ code: SpanStatusCode.ERROR, message: error?.message })
}
